import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Package, Trash2, Check, Paintbrush, Pencil, Search, Loader2, X, AlertTriangle } from "lucide-react";
import { SelectedOrderProductData } from "@/types/notifications";
import { useDebounce } from "@/hooks/useDebounce";

interface InventoryResult {
  product_id: string;
  product_naam: string;
  eenheid: string;
  locatie: string;
  voorraad: number;
}

interface InkoopProductReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: SelectedOrderProductData[];
  onConfirm: (products: SelectedOrderProductData[]) => void;
  onCancel: () => void;
}

const InkoopProductReview: React.FC<InkoopProductReviewProps> = ({
  open,
  onOpenChange,
  products,
  onConfirm,
  onCancel,
}) => {
  const [editableProducts, setEditableProducts] = useState<SelectedOrderProductData[]>([]);
  const [editingBasePaintSku, setEditingBasePaintSku] = useState<string | null>(null);
  const [bpSearchTerm, setBpSearchTerm] = useState("");
  const [bpResults, setBpResults] = useState<InventoryResult[]>([]);
  const [bpSearching, setBpSearching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const debouncedBpSearch = useDebounce(bpSearchTerm, 250);

  useEffect(() => {
    if (open) {
      setEditableProducts(products.map((p) => ({ ...p })));
      setEditingBasePaintSku(null);
      setBpSearchTerm("");
      setBpResults([]);
    }
  }, [open, products]);

  // Search inventory for base paint correction
  useEffect(() => {
    if (!debouncedBpSearch || debouncedBpSearch.length < 2) {
      setBpResults([]);
      return;
    }
    const search = async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setBpSearching(true);
      try {
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const sessionToken = localStorage.getItem('auth_session_token');
        const params = new URLSearchParams({ action: 'search_inventory', query: debouncedBpSearch });
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/decoprof-orders-proxy?${params}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-session-token': sessionToken || '',
              'apikey': anonKey,
              'Authorization': `Bearer ${anonKey}`,
            },
            signal: controller.signal,
          }
        );
        if (!res.ok) throw new Error('Zoekfout');
        const json = await res.json();
        setBpResults(json.data || []);
      } catch (e: any) {
        if (e.name !== 'AbortError') setBpResults([]);
      } finally {
        setBpSearching(false);
      }
    };
    search();
  }, [debouncedBpSearch]);

  const handleRemove = (sku: string) => {
    setEditableProducts((prev) => prev.filter((p) => p.sku !== sku));
  };

  const handleQuantityChange = (sku: string, quantity: number) => {
    setEditableProducts((prev) =>
      prev.map((p) => (p.sku === sku ? { ...p, quantity: Math.max(1, quantity) } : p))
    );
  };

  const handleColorChange = (sku: string, color: string) => {
    setEditableProducts((prev) =>
      prev.map((p) => (p.sku === sku ? { ...p, color: color || null } : p))
    );
  };

  const handleSelectBasePaint = (productSku: string, result: InventoryResult) => {
    setEditableProducts((prev) =>
      prev.map((p) =>
        p.sku === productSku
          ? { ...p, basePaintName: result.product_naam, basePaintSku: result.product_id }
          : p
      )
    );
    setEditingBasePaintSku(null);
    setBpSearchTerm("");
    setBpResults([]);
  };

  const handleConfirm = () => {
    if (editableProducts.length === 0) {
      onCancel();
      return;
    }
    onConfirm(editableProducts);
  };

  const openBasePaintEdit = (sku: string) => {
    setEditingBasePaintSku(sku);
    setBpSearchTerm("");
    setBpResults([]);
  };

  const closeBasePaintEdit = () => {
    setEditingBasePaintSku(null);
    setBpSearchTerm("");
    setBpResults([]);
    abortRef.current?.abort();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Inkoop producten controleren
          </DialogTitle>
          <DialogDescription>
            Controleer de producten voordat de inkoop e-mail wordt verzonden.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {editableProducts.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              Geen producten meer. De inkoop wordt geannuleerd.
            </div>
          ) : (
            editableProducts.map((product) => (
              <div
                key={product.sku}
                className="p-3 rounded-lg border bg-card space-y-2"
              >
                {/* Product header */}
                <div className="flex items-start gap-3">
                  {product.imageUrl && (
                    <img
                      src={product.imageUrl}
                      alt={product.productTitle}
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{product.productTitle}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <Badge variant="outline" className="text-[10px] h-5">
                            {product.sku}
                          </Badge>
                          {product.eenheid && (
                            <Badge variant="secondary" className="text-[10px] h-5">
                              {product.eenheid}
                            </Badge>
                          )}
                          {product.variantTitle && (
                            <span className="text-xs text-muted-foreground truncate">
                              {product.variantTitle}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemove(product.sku)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Base paint block */}
                {product.basePaintName && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Paintbrush className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                          Basisverf
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900"
                        onClick={() => openBasePaintEdit(product.sku)}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Wijzig
                      </Button>
                    </div>
                    <p className="text-sm font-medium mt-1 truncate">{product.basePaintName}</p>
                    {product.basePaintSku && (
                      <Badge variant="outline" className="text-[10px] h-5 mt-1 border-amber-300 dark:border-amber-700">
                        Art: {product.basePaintSku}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Missing base paint warning */}
                {product.sku?.toLowerCase().includes('kx') && !product.basePaintName && editingBasePaintSku !== product.sku && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-destructive">
                            Basisverf ontbreekt
                          </span>
                          <p className="text-xs text-muted-foreground">
                            Dit kleurproduct heeft een basisverf nodig.
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-xs border-destructive/30 text-destructive hover:bg-destructive/10"
                        onClick={() => openBasePaintEdit(product.sku)}
                      >
                        Toevoegen
                      </Button>
                    </div>
                  </div>
                )}

                {/* Base paint inline search */}
                {editingBasePaintSku === product.sku && (
                  <div className="rounded-md border border-primary/30 bg-muted/50 p-2.5 space-y-2">
                    <div className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-muted-foreground" />
                      <Input
                        autoFocus
                        placeholder="Zoek basisverf in voorraad..."
                        value={bpSearchTerm}
                        onChange={(e) => setBpSearchTerm(e.target.value)}
                        className="h-8 text-sm flex-1"
                      />
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={closeBasePaintEdit}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {bpSearching && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> Zoeken...
                      </div>
                    )}
                    {bpResults.length > 0 && (
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {bpResults.map((r) => (
                          <button
                            key={`${r.product_id}-${r.locatie}`}
                            className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-accent transition-colors"
                            onClick={() => handleSelectBasePaint(product.sku, r)}
                          >
                            <span className="font-medium">{r.product_naam}</span>
                            <span className="text-xs text-muted-foreground ml-2">{r.product_id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {!bpSearching && debouncedBpSearch.length >= 2 && bpResults.length === 0 && (
                      <p className="text-xs text-muted-foreground py-1">Geen resultaten</p>
                    )}
                  </div>
                )}

                {/* Quantity & color */}
                <div className="flex items-center gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground font-medium">Aantal</label>
                    <Input
                      type="number"
                      min={1}
                      value={product.quantity}
                      onChange={(e) => handleQuantityChange(product.sku, parseInt(e.target.value) || 1)}
                      className="h-8 w-20 text-sm"
                    />
                  </div>
                  <div className="space-y-1 flex-1">
                    <label className="text-[11px] text-muted-foreground font-medium">Kleur</label>
                    <Input
                      type="text"
                      value={product.color || ""}
                      onChange={(e) => handleColorChange(product.sku, e.target.value)}
                      placeholder="Geen kleur"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel}>
            Annuleren
          </Button>
          <Button onClick={handleConfirm} disabled={editableProducts.length === 0}>
            <Check className="h-4 w-4 mr-1" />
            Bevestigen ({editableProducts.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InkoopProductReview;
